/**
 * Timeline route - Pain history view
 *
 * Protected route showing chronological pain records with multiple view modes:
 * - Days: Scrollable list grouped by month (default)
 * - Week: Weekly summary with aggregate heatmap
 * - Month: Calendar view with heatmap (future)
 *
 * Events are displayed as interactive markers that open a detail dialog.
 */
import { useState, useMemo, useCallback, useEffect } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { GitCompare } from "lucide-react";
import { formatLocalDateYmd } from "@floline/journal-aggregation";
import { useTimelineRecords, useTimelineEvents, useEvent, useUpdateEvent, useDeleteEvent, useMedications } from "@/hooks";
import {
  TimelineDaysView,
  TimelineEmptyState,
  TimelineWeekView,
  TimelineMonthView,
  TimelineFilterPanel,
  type TimelineFilters,
  DEFAULT_TIMELINE_FILTERS,
  applyTimelineFilters,
  hasActiveFilters,
  getWeekStartDate,
  getWeekEndDate,
  getMonthStartDate,
  expandDateRange,
  filterRecordsForWeek,
  filterRecordsForCalendarGrid,
  formatLocalDate,
} from "@/components/timeline";
import { EventDetailDialog } from "@/components/events/event-detail-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** View mode types for timeline */
type TimelineViewMode = "days" | "week" | "month";

function parseLocalYmd(dateStr: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  // Noon local time avoids DST edge cases.
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function parseViewMode(value: unknown): TimelineViewMode {
  return value === "week" || value === "month" || value === "days" ? value : "days";
}

/**
 * Loading skeleton for timeline
 */
function TimelineLoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Month header skeleton */}
      <div className="h-8 w-32 bg-muted rounded" />
      
      {/* Card skeletons */}
      {[1, 2, 3].map((i) => (
        <Card key={i} className="w-full">
          <CardContent className="p-4">
            <div className="flex gap-4">
              {/* Body diagram placeholder */}
              <div className="w-20 h-24 bg-muted rounded flex-shrink-0" />
              
              {/* Text placeholders */}
              <div className="flex-1 space-y-3">
                <div className="h-5 w-32 bg-muted rounded" />
                <div className="h-4 w-24 bg-muted rounded" />
                <div className="h-4 w-40 bg-muted rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Error state component
 */
function TimelineErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("timeline");
  return (
    <Card className="w-full">
      <CardContent className="flex flex-col items-center space-y-4 py-12">
        <div className="text-center space-y-2">
          <h2 className="text-lg font-semibold text-destructive">
            {t("error.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("error.description")}
          </p>
        </div>
        <Button onClick={onRetry}>{t("error.retry")}</Button>
      </CardContent>
    </Card>
  );
}

/**
 * View mode toggle component
 */
function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: TimelineViewMode;
  onChange: (mode: TimelineViewMode) => void;
}) {
  const { t } = useTranslation("timeline");
  return (
    <>
      <div className="md:hidden w-full">
        <Select value={mode} onValueChange={(value) => onChange(value as TimelineViewMode)}>
          <SelectTrigger aria-label={t("viewMode.ariaLabel")} className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="days">{t("viewMode.days")}</SelectItem>
            <SelectItem value="week">{t("viewMode.week")}</SelectItem>
            <SelectItem value="month">{t("viewMode.month")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="hidden md:flex justify-center gap-1 p-1 bg-muted rounded-lg">
        <Button
          variant={mode === "days" ? "default" : "ghost"}
          size="sm"
          onClick={() => onChange("days")}
          aria-pressed={mode === "days"}
        >
          {t("viewMode.days")}
        </Button>
        <Button
          variant={mode === "week" ? "default" : "ghost"}
          size="sm"
          onClick={() => onChange("week")}
          aria-pressed={mode === "week"}
        >
          {t("viewMode.week")}
        </Button>
        <Button
          variant={mode === "month" ? "default" : "ghost"}
          size="sm"
          onClick={() => onChange("month")}
          aria-pressed={mode === "month"}
        >
          {t("viewMode.month")}
        </Button>
      </div>
    </>
  );
}

function TimelineHeader({
  viewMode,
  onViewModeChange,
}: {
  viewMode: TimelineViewMode;
  onViewModeChange: (mode: TimelineViewMode) => void;
}) {
  const { t } = useTranslation("timeline");
  return (
    <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
      <h1 className="text-2xl font-bold">{t("title")}</h1>
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
        <Link to="/events/compare" className="w-full sm:w-auto">
          <Button variant="outline" size="sm" className="h-9 w-full sm:w-auto">
            <GitCompare className="h-4 w-4 mr-1" />
            {t("compare")}
          </Button>
        </Link>
        <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
      </div>
    </div>
  );
}

/**
 * Timeline page component
 * 
 * Shows chronological list of daily pain records with:
 * - Filter panel for filtering by location and medication
 * - View mode toggle (Days/Week/Month)
 * - Days: Monthly grouping with infinite scrolling
 * - Week: Weekly summary with navigation
 * - Empty state for new users
 * - Loading and error states
 */
function TimelinePage() {
  const navigate = useNavigate();
  const { t } = useTranslation("timeline");
  const search = Route.useSearch();
  
  // View mode state (synced to URL so mobile Back steps through changes)
  const [viewMode, setViewMode] = useState<TimelineViewMode>(() =>
    parseViewMode(search.view)
  );
  
  // Filter state
  const [filters, setFilters] = useState<TimelineFilters>(DEFAULT_TIMELINE_FILTERS);
  
  // Week view state - tracks the currently selected week
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const fromSearch = typeof search.week === "string" ? parseLocalYmd(search.week) : null;
    return getWeekStartDate(fromSearch ?? new Date());
  });
  
  // Month view state - tracks the currently displayed month
  const [monthDate, setMonthDate] = useState(() => {
    const fromSearch = typeof search.month === "string" ? parseLocalYmd(search.month) : null;
    return getMonthStartDate(fromSearch ?? new Date());
  });

  // Keep local state in sync when the user navigates via Back/Forward.
  useEffect(() => {
    setViewMode(parseViewMode(search.view));
  }, [search.view]);

  useEffect(() => {
    if (typeof search.week !== "string") return;
    const d = parseLocalYmd(search.week);
    if (!d) return;
    setWeekStartDate(getWeekStartDate(d));
  }, [search.week]);

  useEffect(() => {
    if (typeof search.month !== "string") return;
    const d = parseLocalYmd(search.month);
    if (!d) return;
    setMonthDate(getMonthStartDate(d));
  }, [search.month]);

  const handleViewModeChange = useCallback(
    (mode: TimelineViewMode) => {
      setViewMode(mode);
      void navigate({
        to: "/timeline",
        search: (prev) => ({
          ...prev,
          view: mode === "days" ? undefined : mode,
          week: mode === "week" ? formatLocalDate(weekStartDate) : prev.week,
          month: mode === "month" ? formatLocalDate(monthDate) : prev.month,
        }),
      });
    },
    [navigate, weekStartDate, monthDate]
  );

  const handleWeekChange = useCallback(
    (nextWeekStart: Date) => {
      setWeekStartDate(nextWeekStart);
      void navigate({
        to: "/timeline",
        search: (prev) => ({
          ...prev,
          view: "week",
          week: formatLocalDate(nextWeekStart),
        }),
      });
    },
    [navigate]
  );

  const handleMonthChange = useCallback(
    (nextMonth: Date) => {
      setMonthDate(nextMonth);
      void navigate({
        to: "/timeline",
        search: (prev) => ({
          ...prev,
          view: "month",
          month: formatLocalDate(nextMonth),
        }),
      });
    },
    [navigate]
  );

  // Event detail dialog state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  const {
    records,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useTimelineRecords();

  // Fetch selected event details when an event is selected
  const { event: selectedEvent } = useEvent(selectedEventId);
  
  // Event mutation hooks
  const { updateEvent, isUpdating } = useUpdateEvent();
  const { deleteEvent, isDeleting } = useDeleteEvent();

  // Calculate date range for events based on loaded records
  const eventDateRange = useMemo(() => {
    if (records.length === 0) {
      // Default to current week range when no records
      const weekEnd = getWeekEndDate(weekStartDate);
      return {
        startDate: formatLocalDateYmd(weekStartDate),
        endDate: formatLocalDateYmd(weekEnd),
      };
    }
    
    // Get date range from records and expand to catch events that span boundaries
    const recordDates = records.map((r) => r.recordDate);
    const sorted = [...recordDates].sort();
    return expandDateRange(sorted[0], sorted[sorted.length - 1], 7);
  }, [records, weekStartDate]);

  // Fetch events for the timeline date range
  const { eventsByDate, isLoading: eventsLoading } = useTimelineEvents(
    eventDateRange.startDate,
    eventDateRange.endDate
  );

  // Fetch all user medications for duration indicators (T2.8)
  const { medications, isLoading: medicationsLoading } = useMedications({ status: "all" });

  // Filter records for week view
  const weekRecords = useMemo(() => {
    return filterRecordsForWeek(records, weekStartDate);
  }, [records, weekStartDate]);
  
  // Filter records for month view - use calendar grid filter to include padding days
  // The component internally filters for month summary, but needs all visible grid records
  const monthRecords = useMemo(() => {
    return filterRecordsForCalendarGrid(records, monthDate.getFullYear(), monthDate.getMonth());
  }, [records, monthDate]);

  // Get the oldest loaded record date to determine if we need to fetch more
  const oldestLoadedDate = useMemo(() => {
    if (records.length === 0) return null;
    const dates = records.map((r) => r.recordDate).sort();
    return dates[0];
  }, [records]);

  // Auto-fetch more data when navigating to past weeks/months without data
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isLoading) return;

    // Determine the target date we need data for
    let targetDate: string | null = null;
    
    if (viewMode === "week") {
      // Get the Monday of the selected week
      targetDate = formatLocalDate(weekStartDate);
    } else if (viewMode === "month") {
      // Get the first day of the selected month
      targetDate = formatLocalDate(monthDate);
    }

    // If we don't have a target date or no loaded records, skip
    if (!targetDate || !oldestLoadedDate) return;

    // If the target date is before our oldest loaded data, fetch more
    if (targetDate < oldestLoadedDate) {
      void fetchNextPage();
    }
  }, [
    viewMode,
    weekStartDate,
    monthDate,
    oldestLoadedDate,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
  ]);

  // Apply user filters to each view's records
  const filteredRecords = useMemo(
    () => applyTimelineFilters(records, filters),
    [records, filters]
  );
  
  const filteredWeekRecords = useMemo(
    () => applyTimelineFilters(weekRecords, filters),
    [weekRecords, filters]
  );
  
  const filteredMonthRecords = useMemo(
    () => applyTimelineFilters(monthRecords, filters),
    [monthRecords, filters]
  );

  // Navigate to daily view
  const handleDayClick = (date: string) => {
    void navigate({ to: "/daily/$date", params: { date } });
  };

  // Navigate to check-in with date pre-filled (for empty/unlogged days)
  const handleEmptyDayClick = useCallback((date: string) => {
    void navigate({ to: "/check-in", search: { date } });
  }, [navigate]);

  // Handle event selection from timeline marker
  const handleEventSelect = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
    setEventDialogOpen(true);
  }, []);

  // Handle closing the event dialog
  const handleEventDialogClose = useCallback((open: boolean) => {
    setEventDialogOpen(open);
    if (!open) {
      // Clear selection when dialog closes
      setSelectedEventId(null);
    }
  }, []);

  // Wrapper for update event to match EventDetailDialog signature
  const handleUpdateEvent = useCallback(
    async (id: string, data: Parameters<typeof updateEvent>[0]["data"]) => {
      await updateEvent({ id, data });
    },
    [updateEvent]
  );

  // Wrapper for delete event to match EventDetailDialog signature
  const handleDeleteEvent = useCallback(
    async (id: string) => {
      await deleteEvent(id);
    },
    [deleteEvent]
  );

  // Loading state (wait for records, events, and medications)
  if (isLoading || eventsLoading || medicationsLoading) {
    return (
      <section className="container max-w-2xl mx-auto px-4 py-6">
        <TimelineHeader viewMode={viewMode} onViewModeChange={handleViewModeChange} />
        <TimelineLoadingSkeleton />
      </section>
    );
  }

  // Error state
  if (isError) {
    return (
      <section className="container max-w-2xl mx-auto px-4 py-6">
        <TimelineHeader viewMode={viewMode} onViewModeChange={handleViewModeChange} />
        <TimelineErrorState onRetry={() => refetch()} />
      </section>
    );
  }

  // Empty state - no records yet (only for days view)
  if (records.length === 0 && viewMode === "days") {
    return (
      <section className="container max-w-2xl mx-auto px-4 py-6">
        <TimelineHeader viewMode={viewMode} onViewModeChange={handleViewModeChange} />
        <TimelineEmptyState />
      </section>
    );
  }

  // Render based on view mode
  return (
    <section className="container max-w-2xl mx-auto px-4 py-6">
      <TimelineHeader viewMode={viewMode} onViewModeChange={handleViewModeChange} />

      {/* Filter panel - only show when there are records to filter */}
      {records.length > 0 && (
        <TimelineFilterPanel
          records={records}
          filters={filters}
          onFiltersChange={setFilters}
          className="mb-4"
        />
      )}

      {/* Empty filter results message */}
      {hasActiveFilters(filters) && (
        (viewMode === "days" && filteredRecords.length === 0) ||
        (viewMode === "week" && filteredWeekRecords.length === 0) ||
        (viewMode === "month" && filteredMonthRecords.length === 0)
      ) && (
        <Card className="w-full">
          <CardContent className="flex flex-col items-center space-y-2 py-8">
            <p className="text-muted-foreground text-center">
              {t("filters.noMatch")}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(DEFAULT_TIMELINE_FILTERS)}
            >
              {t("filters.clearFilters")}
            </Button>
          </CardContent>
        </Card>
      )}

      {viewMode === "days" && (
        <TimelineDaysView
          records={filteredRecords}
          allRecords={records}
          eventsByDate={eventsByDate}
          medications={medications}
          onEventSelect={handleEventSelect}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      )}

      {viewMode === "week" && (
        <TimelineWeekView
          records={filteredWeekRecords}
          allRecords={records}
          eventsByDate={eventsByDate}
          weekStartDate={weekStartDate}
          onWeekChange={handleWeekChange}
          onDayClick={handleDayClick}
          onEmptyDayClick={handleEmptyDayClick}
          onAddEntry={() => void navigate({ to: "/check-in" })}
        />
      )}

      {viewMode === "month" && (
        <TimelineMonthView
          records={filteredMonthRecords}
          allRecords={records}
          eventsByDate={eventsByDate}
          monthDate={monthDate}
          onMonthChange={handleMonthChange}
          onDayClick={handleDayClick}
          onEmptyDayClick={handleEmptyDayClick}
          onEventSelect={handleEventSelect}
          onAddEntry={() => void navigate({ to: "/check-in" })}
        />
      )}

      {/* Event Detail Dialog */}
      <EventDetailDialog
        event={selectedEvent}
        open={eventDialogOpen}
        onOpenChange={handleEventDialogClose}
        onUpdate={handleUpdateEvent}
        onDelete={handleDeleteEvent}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />
    </section>
  );
}

export const Route = createFileRoute("/_authenticated/timeline")({
  component: TimelinePage,
  validateSearch: (search: Record<string, unknown>) => {
    const result: { view?: TimelineViewMode; week?: string; month?: string } = {};
    if (search.view === "days" || search.view === "week" || search.view === "month") {
      result.view = search.view;
    }
    if (typeof search.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.week)) {
      result.week = search.week;
    }
    if (
      typeof search.month === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(search.month)
    ) {
      result.month = search.month;
    }
    return result;
  },
});
